import axios, { AxiosHeaders } from "axios";
import jwt_decode from "jwt-decode";
import { useNavigate } from "react-router-dom";
import { useRestaurantData } from "../provider/RestaurantDataContext";
import { useCallback, useRef, useState } from "react";
import { QrMenuApi } from "../provider/queryClient";

export type ToolOptions = {
    goBack: (twice: boolean) => void;
    handleToken: (token: string) => Promise<boolean>;
}

export interface ImageType {
    type: string
}

type RealmAccess = {
    roles: string[];
}

export type TokenFields = {
    given_name: string;
    family_name: string;
    email: string;
    email_verified: number;
    preferred_username: string;
    exp: number;
    iat: number;
    iss: number;
    name: string;
    sub: string;
    realm_access: RealmAccess;
    restaurant_ids: number[];
}

export function useTools(): ToolOptions {
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const navigate = useNavigate();
    const [currentRequest, setCurrentRequest] = useState<Promise<boolean> | undefined>();
    const reuqestPending = useRef<boolean>(false);
    const { setToken, token: storedToken, setDecodedToken, decodedToken } = useRestaurantData();

    const goBack = (twice: boolean) => {
        if (twice) {
            navigate('../..', { relative: 'path' });
        } else {
            navigate('..', { relative: 'path' });
        }
    }

    const handleToken = useCallback((token: string): Promise<boolean> => {
        if (token === storedToken && decodedToken) {
            // check if token is the same and if it is still not timed out then do not revalidate it
            if (decodedToken.exp * 1000 - Date.now() > 5 * 1000 * 60) {
                return new Promise<boolean>((resolve) => resolve(true));
            }
        }
        const headers = new AxiosHeaders();
        headers.setAuthorization('Bearer ' + token);
        if (reuqestPending.current && currentRequest) {
            return currentRequest;
        }
        const request = new Promise<boolean>((resolve) => {
            reuqestPending.current = true;
            axios.get(backendUrl + '/restaurants/validateToken', {
                headers
            }).then(_response => {
                console.log('Token validated');
                setToken(token);
                setDecodedToken(jwt_decode(token));
                QrMenuApi.defaults.headers.common['Authorization'] = 'Bearer ' + token;
                resolve(true);
            }).catch(err => {
                console.error('Error validating token', err);
                setToken('');
                setDecodedToken(undefined);
                resolve(false);
            }).finally(() => {
                reuqestPending.current = false;
            })
        });
        setCurrentRequest(request);
        return request;
    }, [decodedToken, backendUrl, setDecodedToken, storedToken, setToken, currentRequest]);

    return { goBack, handleToken };
}
